import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { LibraryFilters } from '@/features/publicsite/registry/LibraryFilters';
import { libraryPageHref, SOURCE_KEY } from '@/features/publicsite/registry/libraryQuery';
import { useExternalCatalogues } from '@/features/common/useExternalCatalogues';
import { Pagination } from '@/features/common/Pagination';
import { useSession } from '@/app/session';
import { useTranslation } from '@/i18n/useTranslation';
import { LibraryExternalCatalogues } from '@/features/publicsite/registry/LibraryExternalCatalogues';
import {
  isLibraryFiltered,
  libraryEmptyKey,
  linkableCatalogues,
} from '@/features/publicsite/registry/libraryListing';
import { LibraryResourceCard } from '@/features/publicsite/registry/LibraryResourceCard';
import { LIBRARY_SOURCE } from '@/features/publicsite/registry/librarySource';
import { useLibraryPage } from '@/features/publicsite/registry/useLibraryPage';
import { useSavedLibraryIds } from '@/features/publicsite/registry/useSavedLibraryIds';

export function LibraryPage() {
  const { t } = useTranslation(publicRegistry);
  const [params] = useSearchParams();
  const query = params.get('q') ?? '';
  const source = params.get(SOURCE_KEY) ?? '';
  const filtering = isLibraryFiltered(params);
  const page = Number(params.get('page') ?? '0') || 0;
  // Filtered to the international catalogues, nothing below reads this library's own rows — so
  // they are not asked for, rather than fetched and then thrown away. It also clears the previous
  // answer, which is what keeps a pager for hidden rows off the page.
  const showNational = source !== LIBRARY_SOURCE.international;
  const pageData = useLibraryPage(params, page, showNational);
  const resources = pageData?.items ?? [];
  const hrefForPage = (target: number) => libraryPageHref(params, target);
  // FR-021's third function. Only a signed-in reader has a reading list, and the library itself
  // stays open to anonymous visitors — which is the point of the module — so the button appears
  // for some readers and not others rather than the page changing shape.
  const { user } = useSession();
  const catalogues = linkableCatalogues(useExternalCatalogues(), query);
  const savedIds = useSavedLibraryIds(user);
  const emptyKey = libraryEmptyKey(showNational, filtering);
  // A save said where it happened, with the way to the shelf it went on.
  const [justSaved, setJustSaved] = useState(false);

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={t('libraryHeading')}
      lead={t('libraryLead')}
      // Filtered to the international catalogues, this library's own rows are not the answer to
      // what was asked — so the grid empties rather than showing results the filter excluded.
      items={showNational ? resources : []}
      emptyMessage={emptyKey ? t(emptyKey) : undefined}
      renderCard={(r) => (
        <LibraryResourceCard
          key={r.id}
          resource={r}
          canSave={user !== null}
          saved={savedIds.has(r.id)}
          onSavedChange={setJustSaved}
        />
      )}
      // Below the results, where a pager belongs. It used to be the first of this section's
      // children, so it announced page 2 above the filters and above the rows it pages — an
      // artifact of the wrapper having only a slot above the grid, not a decision.
      after={
        <>
          {showNational && (
            <Pagination
              page={page}
              totalPages={pageData?.totalPages ?? 0}
              hrefFor={hrefForPage}
              labels={{
                region: t('paginationAria'),
                previous: t('paginationPrevious'),
                next: t('paginationNext'),
                page: (n) => t('paginationPageAria', { page: n }),
                current: (n) => t('paginationCurrentAria', { page: n }),
              }}
            />
          )}
          {source !== LIBRARY_SOURCE.national && catalogues.length > 0 && (
            <LibraryExternalCatalogues catalogues={catalogues} />
          )}
        </>
      }
    >
      <LibraryFilters />
      {/* For a signed-in reader only, beside the search: where what they save here goes. */}
      {user && (
        <p className="library-saved-link">
          <Link to="/app/biblioteca">{t('librarySavedLink')}</Link>
        </p>
      )}
      {user && justSaved && (
        <p className="message message--inline message--success" role="status">
          <span>
            {t('librarySavedNotice')} <Link to="/app/biblioteca">{t('librarySavedLink')}</Link>
          </span>
        </p>
      )}
      {showNational && <span className="eyebrow catalog-group">{t('libraryNationalEyebrow')}</span>}
    </CatalogSection>
  );
}
